import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getInterviewQuestions, logQuestionTransition } from '../../services/interview.service';
import { QuestionItem } from '../../types/interview.types';
import { QuestionTimelineItem } from './QuestionTimelineItem'; // Import the new component
import { getStoredItems, addItem, updateItem, StorageItem } from '../../utils/storage';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '@/components/ui/atoms/Button';
import { useCopyProtection } from '../../hooks/useCopyProtection';
import { Volume2, VolumeX } from 'lucide-react';

// Import the MediaStatus type from useMediaCapture hook
interface MediaStatus {
  isVideoEnabled: boolean;
  isAudioEnabled: boolean;
  isScreenSharingEnabled: boolean;
}

interface QuestionsData {
  questions: QuestionItem[];
}

interface InterviewQuestionsProps {
  interviewId: string;
  startTime: number; // UTC timestamp in seconds
  isInterviewStarted: boolean; // New prop to control visibility based on interview start
  mediaStatus: MediaStatus; // Add mediaStatus to props
  onActiveQuestionChange?: (question: QuestionItem | null) => void; // New prop to notify parent of active question changes
  onQuestionsLoaded?: (questions: QuestionItem[]) => void; // New prop to notify parent when questions are loaded
  onLastQuestionTimeUp?: () => void; // New prop to notify parent when last question time is up
  singleQuestionMode?: boolean; // If true, show only the active question (not timeline)
  nextQuestionTriggerRef?: React.MutableRefObject<(() => void) | null>; // Ref to expose next question trigger
  isRecording?: boolean; // Prop to indicate if recording has started for current question
  // Azure TTS props
  speak?: (text: string) => void;
  stopTTS?: () => void;
  isTTSPlaying?: boolean;
  isMuted?: boolean;
  onToggleMute?: () => void;
}

// Type for interview progress persistence
interface InterviewProgressItem extends StorageItem {
  interviewId: string;
  lastQuestionIndex: number;
  questionStartedAt: number | null;
  updatedAt: string; // ISO string
}

// Gentle two-tone sine-wave chime (replaces the harsh 800Hz ping)
function playGentleChime() {
  // @ts-ignore: webkitAudioContext is for Safari support
  const ctx = new (window.AudioContext || (window as any).webkitAudioContext)();
  const gainNode = ctx.createGain();
  gainNode.connect(ctx.destination);
  [[523.25, 0], [392, 0.25]].forEach(([freq, start]) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.setValueAtTime(freq, ctx.currentTime + start);
    osc.connect(gainNode);
    gainNode.gain.setValueAtTime(0, ctx.currentTime + start);
    gainNode.gain.linearRampToValueAtTime(0.15, ctx.currentTime + start + 0.05);
    gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + start + 0.6);
    osc.start(ctx.currentTime + start);
    osc.stop(ctx.currentTime + start + 0.7);
  });
}

export const InterviewQuestions: React.FC<InterviewQuestionsProps> = ({
  interviewId,
  startTime: _startTime,
  isInterviewStarted,
  mediaStatus,
  onActiveQuestionChange,
  onQuestionsLoaded,
  onLastQuestionTimeUp,
  singleQuestionMode = false,
  nextQuestionTriggerRef,
  isRecording = true,
  speak,
  stopTTS,
  isTTSPlaying = false,
  isMuted = false,
  onToggleMute,
}) => {
  const { t } = useTranslation('interview');
  const [questionsData, setQuestionsData] = useState<QuestionsData | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [activeQuestionIndex, setActiveQuestionIndex] = useState<number>(-1);
  const [allQuestions, setAllQuestions] = useState<QuestionItem[]>([]);

  // Per-question countdown timer state
  const [questionStartedAt, setQuestionStartedAt] = useState<number | null>(null);
  const [_countdownSecondsLeft, setCountdownSecondsLeft] = useState<number>(0);
  const autoAdvanceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chimePlayedRef = useRef<boolean>(false);
  // Ref for advanceToNextQuestion — allows auto-advance timeout to always call latest version
  const advanceToNextQuestionRef = useRef<() => void>(() => {});

  // Anti-cheating: Prevent copying of interview questions
  const {
    onCopy: handleCopyProtection,
    onCut: handleCutProtection,
    onContextMenu: handleContextMenuProtection,
    protectedStyle: copyProtectedStyle,
  } = useCopyProtection({
    enabled: isInterviewStarted,
    showWarning: true,
    warningMessage: t('live.copy_warning'),
  });
  const lastLoggedIndexRef = useRef<number>(-1); // Ref to track the last LOGGED index
  const ttsPlayedForQuestionRef = useRef<number>(-1); // Ref to track which question index TTS has been triggered for
  const [showNextConfirm, setShowNextConfirm] = useState(false);
  const [pendingNext, setPendingNext] = useState(false);
  const questionContainerRef = useRef<HTMLDivElement | null>(null); // Ref for scrolling to top on question change
  const [lastQuestionTimeUpTriggered, setLastQuestionTimeUpTriggered] = useState(false);

  // Fetch questions once when interview starts (no polling - questions don't change during interview)
  useEffect(() => {
    const fetchInterviewQuestions = async () => {
      try {
        setLoading(true);
        setError(null);
        const questionsResponse = await getInterviewQuestions(interviewId);

        if (questionsResponse.error) {
          console.error('[InterviewQuestions] Error fetching questions:', questionsResponse.error);
          setError(`Error: ${questionsResponse.error}`);
        } else if (questionsResponse.data?.questions) {
          setQuestionsData({
            questions: questionsResponse.data.questions
          });

          // Process questions data
          const filteredQuestions = questionsResponse.data.questions
            .filter(q => q.question.trim() !== '')
            .sort((a, b) => a.index - b.index);

          setAllQuestions(filteredQuestions);

          // Notify parent component about loaded questions
          onQuestionsLoaded?.(filteredQuestions);
        }
      } catch (error) {
        console.error('[InterviewQuestions] Error fetching interview questions:', error);
        setError(`Network error: ${error}`);
      } finally {
        setLoading(false);
      }
    };

    // Fetch questions once when interview starts
    if (isInterviewStarted && interviewId) {
      fetchInterviewQuestions();
    }
  }, [interviewId, isInterviewStarted]);

  // --- Per-question countdown: activate timer when activeQuestionIndex changes ---
  useEffect(() => {
    if (activeQuestionIndex < 0 || !allQuestions[activeQuestionIndex]) return;
    const question = allQuestions[activeQuestionIndex];
    const now = Date.now();
    setQuestionStartedAt(now);
    chimePlayedRef.current = false;

    // Persist for page reload resilience
    localStorage.setItem('questionStartedAt', String(now));
    localStorage.setItem('questionStartedAtIndex', String(activeQuestionIndex));

    // Clear previous auto-advance
    if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);

    const durationMs = (question.time_minutes || question.time) * 60 * 1000;
    // Use ref to avoid stale closure — advanceToNextQuestionRef is kept in sync below
    autoAdvanceRef.current = setTimeout(() => {
      advanceToNextQuestionRef.current();
    }, durationMs);

    return () => {
      if (autoAdvanceRef.current) clearTimeout(autoAdvanceRef.current);
    };
  }, [activeQuestionIndex, allQuestions]);

  // --- Countdown display: 1-second interval ---
  useEffect(() => {
    if (!questionStartedAt || activeQuestionIndex < 0 || !allQuestions[activeQuestionIndex]) return;
    const question = allQuestions[activeQuestionIndex];
    const totalSeconds = (question.time_minutes || question.time) * 60;

    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - questionStartedAt) / 1000);
      const remaining = Math.max(0, totalSeconds - elapsed);
      setCountdownSecondsLeft(remaining);

      // Gentle chime at 30s remaining (once)
      if (remaining <= 30 && remaining > 29 && !chimePlayedRef.current) {
        chimePlayedRef.current = true;
        playGentleChime();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [questionStartedAt, activeQuestionIndex, allQuestions]);

  // Effect to log transition and notify parent WHEN activeQuestionIndex changes
  useEffect(() => {
    // Guard conditions: interview started, index is valid, question data exists
    if (!isInterviewStarted || activeQuestionIndex < 0 || !allQuestions[activeQuestionIndex]) {
      // Notify parent that no question is active
      onActiveQuestionChange?.(null);
      return;
    }

    // *** Prevent Duplicate Log Check ***
    if (activeQuestionIndex === lastLoggedIndexRef.current) {
        console.log(`[InterviewQuestions][LogEffect] Skipping log for index ${activeQuestionIndex} (already logged).`);
        return;
    }

    // Mark this index as "being processed" immediately to prevent re-entry
    console.log(`[InterviewQuestions][LogEffect] Marking index ${activeQuestionIndex} as processed.`);
    lastLoggedIndexRef.current = activeQuestionIndex;

    const currentQuestion = allQuestions[activeQuestionIndex];
    console.log(`[InterviewQuestions][LogEffect] Processing change to active index ${activeQuestionIndex}.`);

    // Notify parent component of the active question change
    onActiveQuestionChange?.(currentQuestion);

    // *** Log the Transition ***
    console.log(`[InterviewQuestions][LogEffect] Logging transition for question index ${activeQuestionIndex}:`, currentQuestion);

    if (currentQuestion.index === undefined) {
      console.error(`[InterviewQuestions][LogEffect] Question index is undefined for question:`, currentQuestion);
      return;
    }

    logQuestionTransition(interviewId, currentQuestion)
      .then(response => {
        if (response.error) {
          console.error(`[InterviewQuestions][LogEffect] Failed to log transition for question index ${activeQuestionIndex}:`, response.error);
        } else {
          console.log(`[InterviewQuestions][LogEffect] Successfully logged transition for question index ${activeQuestionIndex}.`);
        }
      })
      .catch(err => {
          console.error(`[InterviewQuestions][LogEffect] Network/Promise error logging transition for question index ${activeQuestionIndex}:`, err);
      });

  // This effect ONLY runs when activeQuestionIndex changes or when interview starts
  }, [activeQuestionIndex, interviewId, isInterviewStarted, allQuestions, onActiveQuestionChange]);

  // Reset TTS played tracking when question changes
  useEffect(() => {
    ttsPlayedForQuestionRef.current = -1;
  }, [activeQuestionIndex]);

  // Scroll to top when a new question becomes active
  useEffect(() => {
    if (activeQuestionIndex >= 0 && questionContainerRef.current) {
      questionContainerRef.current.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
      console.log(`[InterviewQuestions] Scrolled to top for question ${activeQuestionIndex}`);
    }
  }, [activeQuestionIndex]);

  // Monitor last question time up
  useEffect(() => {
    if (!isInterviewStarted || activeQuestionIndex < 0 || !allQuestions[activeQuestionIndex] || !questionStartedAt) {
      return;
    }

    const currentQuestion = allQuestions[activeQuestionIndex];
    const isLastQuestion = activeQuestionIndex === allQuestions.length - 1;

    if (!isLastQuestion || lastQuestionTimeUpTriggered) {
      return;
    }

    const totalDurationMs = (currentQuestion.time_minutes || currentQuestion.time) * 60 * 1000;
    const elapsedMs = Date.now() - questionStartedAt;
    const remainingMs = totalDurationMs - elapsedMs;

    if (remainingMs <= 0) {
      // Time is already up
      console.log('[InterviewQuestions] Last question time is up, triggering interview end');
      setLastQuestionTimeUpTriggered(true);
      onLastQuestionTimeUp?.();
    } else {
      // Set a timeout for when time will be up
      const timeout = setTimeout(() => {
        console.log('[InterviewQuestions] Last question time up timeout triggered, ending interview');
        setLastQuestionTimeUpTriggered(true);
        onLastQuestionTimeUp?.();
      }, remainingMs);

      return () => clearTimeout(timeout);
    }
  }, [activeQuestionIndex, allQuestions, questionStartedAt, isInterviewStarted, lastQuestionTimeUpTriggered, onLastQuestionTimeUp]);

  // Reset lastQuestionTimeUpTriggered when question changes
  useEffect(() => {
    setLastQuestionTimeUpTriggered(false);
  }, [activeQuestionIndex]);

  // Azure TTS auto-play effect for intro and soft skills questions
  // Fires when recording starts for a question (ensures user gesture + recording is active before TTS)
  useEffect(() => {
    // Guard conditions
    if (!isInterviewStarted || activeQuestionIndex < 0 || !allQuestions[activeQuestionIndex] || !isRecording) {
      return;
    }

    // Guard: only play once per question activation
    if (ttsPlayedForQuestionRef.current === activeQuestionIndex) {
      return;
    }

    const currentQuestion = allQuestions[activeQuestionIndex];

    // Auto-play TTS for intro and soft skills questions only
    // Technical questions use the manual "Hear Question" button
    const isIntro = currentQuestion.section === 'intro' || currentQuestion.question_type === 'introduction';
    const isSoftSkills = currentQuestion.section === 'Soft Skills' || currentQuestion.question_type === 'soft';

    if (!isIntro && !isSoftSkills) {
      console.log(`[InterviewQuestions][TTSEffect] No auto-play for section=${currentQuestion.section}, type=${currentQuestion.question_type}`);
      return;
    }

    // Only speak the text prompt — never code_snippet
    const textToSpeak = currentQuestion.question;
    // Small delay to let recording settle before TTS starts
    const timeoutId = setTimeout(() => {
      ttsPlayedForQuestionRef.current = activeQuestionIndex;
      speak?.(textToSpeak);
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [isRecording, activeQuestionIndex, allQuestions, isInterviewStarted, speak]);

  // --- Define advanceToNextQuestion (internal, not requiring confirm dialog) ---
  const advanceToNextQuestion = useCallback(() => {
    // Stop any currently playing TTS when advancing to next question
    stopTTS?.();
    const nextIndex = activeQuestionIndex + 1;
    if (nextIndex < allQuestions.length) {
      setActiveQuestionIndex(nextIndex);
    }
  }, [activeQuestionIndex, allQuestions.length, stopTTS]);

  // Keep advanceToNextQuestionRef in sync with latest advanceToNextQuestion
  useEffect(() => {
    advanceToNextQuestionRef.current = advanceToNextQuestion;
  }, [advanceToNextQuestion]);

  // --- Define Callbacks Early (Before potential returns) ---
  const handleNextQuestion = useCallback(() => {
    setShowNextConfirm(true);
  }, []);

  // Expose handleNextQuestion to parent via ref
  useEffect(() => {
    if (nextQuestionTriggerRef) {
      nextQuestionTriggerRef.current = handleNextQuestion;
    }
  }, [handleNextQuestion, nextQuestionTriggerRef]);

  const confirmNextQuestion = useCallback(() => {
    setShowNextConfirm(false);
    setPendingNext(true);
    // Clear the auto-advance timeout — user advanced manually
    if (autoAdvanceRef.current) {
      clearTimeout(autoAdvanceRef.current);
      autoAdvanceRef.current = null;
    }
    // Stop any currently playing TTS when advancing to next question
    stopTTS?.();
    const nextIndex = activeQuestionIndex + 1;
    if (nextIndex < allQuestions.length) {
      // Compute time_spent clamped to time_allocated — never exceeds allocation
      const currentQuestion = allQuestions[activeQuestionIndex];
      const timeAllocated = (currentQuestion.time_minutes || currentQuestion.time) * 60;
      const timeSpent = questionStartedAt
        ? Math.min(Math.floor((Date.now() - questionStartedAt) / 1000), timeAllocated)
        : timeAllocated;
      console.log(`[InterviewQuestions] Manual advance: question ${activeQuestionIndex}, time_spent=${timeSpent}s, time_allocated=${timeAllocated}s`);
      // Include enriched fields in response log
      console.log(`[InterviewQuestions] Question response: section=${currentQuestion.section}, skill_area=${currentQuestion.skill_area}, personalization_flag=${currentQuestion.personalization_flag}`);
      setActiveQuestionIndex(nextIndex);
    }
    setPendingNext(false);
  }, [activeQuestionIndex, allQuestions, questionStartedAt, stopTTS]);

  const cancelNextQuestion = useCallback(() => {
    setShowNextConfirm(false);
  }, []);
  // ---------------------------------------------------------

  // Restore last question index from local storage when questions are loaded
  useEffect(() => {
    if (!interviewId || allQuestions.length === 0) return;
    // Try to get progress for this interview
    const progressItems = getStoredItems<InterviewProgressItem>().active;
    const progress = progressItems.find(item => item.interviewId === interviewId);
    if (progress && typeof progress.lastQuestionIndex === 'number') {
      // Clamp to available questions
      const clampedIndex = Math.min(progress.lastQuestionIndex, allQuestions.length - 1);
      if (clampedIndex !== activeQuestionIndex) {
        setActiveQuestionIndex(clampedIndex);
      }
      // Restore questionStartedAt for page reload resilience
      const savedStartedAt = localStorage.getItem('questionStartedAt');
      const savedStartedAtIndex = localStorage.getItem('questionStartedAtIndex');
      if (savedStartedAt && savedStartedAtIndex === String(clampedIndex)) {
        const savedTime = parseInt(savedStartedAt, 10);
        const question = allQuestions[clampedIndex];
        const elapsed = Math.floor((Date.now() - savedTime) / 1000);
        const totalSeconds = (question.time_minutes || question.time) * 60;
        if (elapsed < totalSeconds) {
          setQuestionStartedAt(savedTime); // resume from saved
          console.log(`[InterviewQuestions] Resumed question ${clampedIndex} countdown from localStorage (${elapsed}s elapsed)`);
        }
      }
    } else {
      // No progress stored — start from first question
      if (activeQuestionIndex < 0 && allQuestions.length > 0) {
        setActiveQuestionIndex(0);
      }
    }
    // Only run when questions are loaded or interviewId changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interviewId, allQuestions.length]);

  // Persist progress to local storage whenever activeQuestionIndex changes and is valid
  useEffect(() => {
    if (!interviewId || activeQuestionIndex < 0 || allQuestions.length === 0) return;
    const progressId = `progress_${interviewId}`;
    const progressItem: InterviewProgressItem = {
      id: progressId,
      interviewId,
      lastQuestionIndex: activeQuestionIndex,
      questionStartedAt: questionStartedAt,
      updatedAt: new Date().toISOString(),
    };
    // Check if already exists
    const existing = getStoredItems<InterviewProgressItem>().active.find(item => item.id === progressId);
    if (existing) {
      updateItem<InterviewProgressItem>({ ...existing, ...progressItem });
    } else {
      addItem<InterviewProgressItem>(progressItem);
    }
  }, [interviewId, activeQuestionIndex, questionStartedAt, allQuestions.length]);

  // If interview hasn't started yet, don't render anything
  if (!isInterviewStarted) {
    return null;
  }

  // Check if active question has audio
  const activeQuestion = activeQuestionIndex >= 0 && allQuestions[activeQuestionIndex];

  // Helper function to determine question status and time info
  const getQuestionStatus = (question: QuestionItem, index: number) => {
    const isActive = index === activeQuestionIndex;
    const isCompleted = index < activeQuestionIndex;

    let timeRemaining = 0;
    if (isActive && questionStartedAt) {
      const totalDurationMs = (question.time_minutes || question.time) * 60 * 1000;
      const elapsedOnThisQuestionMs = Date.now() - questionStartedAt;
      const remainingMs = totalDurationMs - elapsedOnThisQuestionMs;
      timeRemaining = Math.max(0, Math.ceil(remainingMs / (1000 * 60)));
    } else if (isActive) {
      timeRemaining = question.time_minutes || question.time;
    }

    return { isCompleted, isActive, timeRemaining };
  };

  // Filter questions to only show current and previous ones
  const visibleQuestions = allQuestions.filter((_, index) => index <= activeQuestionIndex);

  // Show loading state
  if (loading && !questionsData?.questions?.length) {
    return (
      <div className="bg-white rounded-lg shadow-lg flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">Loading interview questions...</p>
        </div>
      </div>
    );
  }

  // Show error state
  if (error && !questionsData?.questions?.length) {
    return (
      <div className="bg-white rounded-lg shadow-lg flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center flex-col">
          <p className="text-red-500">{error}</p>
          <p className="text-sm text-gray-400">InterviewID: {interviewId || 'Not provided'}</p>
        </div>
      </div>
    );
  }

  // Don't render anything if no questions are available yet
  if (!questionsData?.questions?.length && !loading) {
      return (
        <div className="bg-white rounded-lg shadow-lg flex flex-col h-full">
          <div className="flex-1 flex items-center justify-center">
            <p className="text-gray-500">Waiting for interview questions...</p>
          </div>
        </div>
      );
    }

  // If questions were fetched but all were empty/invalid
  if (allQuestions.length === 0 && !loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg flex flex-col h-full">
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">{t('live.questions.no_valid')}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className="bg-white rounded-lg shadow-lg flex flex-col h-full"
      onCopy={handleCopyProtection}
      onCut={handleCutProtection}
      onContextMenu={handleContextMenuProtection}
      style={copyProtectedStyle}
    >
      {/* Azure TTS handles all audio — no hidden audio element needed */}

      {/* Fixed Header Section - Non-scrollable */}
      <div className="flex-shrink-0 p-4">
        {/* Media Warning - Display when camera or microphone is disabled */}
        {isInterviewStarted && (!mediaStatus.isVideoEnabled || !mediaStatus.isAudioEnabled) && (
          <div className="mb-3 p-3 bg-red-100 border-l-4 border-red-500 text-red-700 rounded-md flex items-center animate-pulse">
            <span className="w-4 h-4 mr-2 rounded-full bg-red-500 animate-ping"></span>
            <div>
              <p className="font-medium">{t('live.questions.media_warning_title')}</p>
              <p className="text-sm">
                {!mediaStatus.isVideoEnabled && !mediaStatus.isAudioEnabled
                  ? t('live.questions.media_both_off')
                  : !mediaStatus.isVideoEnabled
                    ? t('live.questions.media_camera_off')
                    : t('live.questions.media_mic_off')}
              </p>
            </div>
          </div>
        )}

        {/* Interview progress header with Next Question button */}
        <div className="mb-4 pb-2 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h2 className="text-lg font-medium text-primary">{t('live.questions.title')}</h2>
              {onToggleMute && (
                <Button
                  type="icon"
                  intent="muted"
                  size="sm"
                  onClick={() => {
                    if (isTTSPlaying && stopTTS) {
                      stopTTS();
                    }
                    onToggleMute();
                  }}
                  title={isMuted ? t('live.questions.unmute_voice') : t('live.questions.mute_voice')}
                  aria-label={isMuted ? t('live.questions.unmute_aria') : t('live.questions.mute_aria')}
                  icon={ isMuted ? ( <VolumeX className="text-gray-400"/> ) : ( <Volume2 className={`${isTTSPlaying ? 'text-blue-500 animate-pulse' : 'text-gray-600'}`}/> ) }
                />
              )}
            </div>
            {/* Timer is rendered inline in QuestionTimelineItem — no duplicate here */}
          </div>
        </div>
      </div>

      {/* Scrollable Content Area */}
      <div ref={questionContainerRef} className="flex-1 overflow-y-auto">
        {/* Display questions based on mode */}
        <div className="space-y-6 px-6 pb-6 pt-4">
        {/* Handle case where interview started but first question hasn't appeared yet */}
        {activeQuestionIndex < 0 && allQuestions.length > 0 && (
           <div className="text-center py-4">
             <p className="text-gray-500">{t('live.questions.waiting_first')}</p>
           </div>
        )}

        {singleQuestionMode ? (
          /* Single Question Mode - Show only active question */
          activeQuestion ? (
            isRecording ? (
              <div className="space-y-6">
                {(() => {
                  const status = getQuestionStatus(activeQuestion, activeQuestionIndex);
                  const uniqueKey = `q-${activeQuestion.index ?? 'undefined'}-pos-${activeQuestionIndex}-${activeQuestion.question_type || 'unknown'}`;

                  return (
                    <QuestionTimelineItem
                      key={uniqueKey}
                      question={activeQuestion}
                      isActive={true}
                      isCompleted={false}
                      timeRemaining={status.timeRemaining}
                      currentQuestionActivationTime={questionStartedAt ?? undefined}
                      questionDurationMinutes={activeQuestion.time_minutes || activeQuestion.time}
                      isLastVisible={true}
                      interviewId={interviewId}
                      speak={speak}
                      stopTTS={stopTTS}
                      isTTSPlaying={isTTSPlaying}
                      isMuted={isMuted}
                    />
                  );
                })()}

              </div>
            ) : (
              /* Skeleton placeholder during question transitions */
              <div className="space-y-3 p-6 animate-pulse">
                <div className="h-3 bg-base-300 rounded w-1/4" />
                <div className="h-5 bg-base-300 rounded w-full" />
                <div className="h-5 bg-base-300 rounded w-5/6" />
                <div className="h-5 bg-base-300 rounded w-4/5" />
                <div className="mt-4 h-8 bg-base-200 rounded w-1/3" />
              </div>
            )
          ) : (
            !loading && (
              <div className="text-center py-4">
                <p className="text-gray-500">{t('live.questions.waiting_next')}</p>
              </div>
            )
          )
        ) : (
          /* Timeline Mode - Show active and previous questions */
          visibleQuestions.length > 0 ? (
            visibleQuestions.map((question, index) => {
              const status = getQuestionStatus(question, index);
              const isLastVisibleItem = index === visibleQuestions.length - 1;

              // Create a truly unique key using both question.index and array position
              const uniqueKey = `q-${question.index ?? 'undefined'}-pos-${index}-${question.question_type || 'unknown'}`;

              return (
                <QuestionTimelineItem
                  key={uniqueKey}
                  question={question}
                  isActive={status.isActive}
                  isCompleted={status.isCompleted}
                  timeRemaining={status.timeRemaining}
                  currentQuestionActivationTime={questionStartedAt ?? undefined}
                  questionDurationMinutes={status.isActive ? (question.time_minutes || question.time) : undefined}
                  isLastVisible={isLastVisibleItem}
                  interviewId={interviewId}
                  speak={speak}
                  stopTTS={stopTTS}
                  isTTSPlaying={isTTSPlaying}
                  isMuted={isMuted}
                />
              );
            })
          ) : (
            // Only show this if loading is done and still no questions
            !loading && (
               <div className="text-center py-4">
                 <p className="text-gray-500">{t('live.questions.will_appear')}</p>
               </div>
             )
          )
        )}


          {/* Message for future questions - timeline mode only */}
          {!singleQuestionMode && activeQuestionIndex >= 0 && allQuestions.length > visibleQuestions.length && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md border border-gray-200">
              <p className="text-sm text-gray-600 flex items-center">
                <span className="mr-2">🔒</span>
                {t('live.questions.future_locked')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Confirmation Dialog - shared across both modes */}
      <Dialog open={showNextConfirm} onOpenChange={setShowNextConfirm}>
        <DialogContent className="max-w-md p-6 border border-gray-200 shadow-lg rounded-lg mx-auto">
          <DialogHeader className="mb-2">
            <DialogTitle className="text-2xl font-bold text-gray-900 mb-2">{t('live.questions.confirm_next_title')}</DialogTitle>
          </DialogHeader>
          <div className="mb-4 text-gray-700 text-sm">
            {t('live.questions.confirm_next_message')}
          </div>
          <div className="flex justify-end gap-2 mt-2">
            <Button
              onClick={cancelNextQuestion}
              label={t('live.actions.cancel')}
              type="outlined"
              intent="secondary"
              disabled={pendingNext}
            />
            <Button
              onClick={confirmNextQuestion}
              label={t('live.questions.confirm')}
              type="contained"
              intent="info"
              disabled={pendingNext}
              className="border"
            />
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
